/**
 * Expert consultation backlog math (ADO 15202 — surfaces the Requested-status
 * backlog that sat invisible from 2026-03-06 to 2026-08-24: 72 requests, oldest
 * 173 days, because nothing ever computed or displayed age against them).
 *
 * `EstimatedResponseTime` (ExpertConsultationService.GetEstimatedResponseTime,
 * TaskManager.Service) is a free-text string shown to the requesting user, not a
 * structured duration — but every value it actually emits fits one of two shapes:
 * "24-48 hours" / "48-72 hours" (a domain-keyed lookup table) or the
 * DefaultResponseTime fallback "48-72 hours". A "72+ hours" shape is reserved on
 * the API side (Legal) even though it isn't wired into ResponseTimeEstimates at
 * time of writing — parsed here defensively in case that changes. Anything else
 * (null, or a shape neither pattern matches) returns `null` rather than a guessed
 * number — an unparseable estimate must never silently count as "not overdue".
 */

import { ConsultationStatus, getConsultations, type ConsultationRecord } from '../services/expertConsultationAdminService';

const RANGE_PATTERN = /(\d+)\s*-\s*(\d+)\s*hours?/i;
const PLUS_PATTERN = /(\d+)\s*\+\s*hours?/i;

/** Upper bound in hours, or null when the text doesn't match a known shape. */
export function parseEstimatedResponseTimeHours(text: string | null | undefined): number | null {
  if (!text) return null;
  const range = RANGE_PATTERN.exec(text);
  if (range) return Number(range[2]);
  const plus = PLUS_PATTERN.exec(text);
  if (plus) return Number(plus[1]);
  return null;
}

/** Whole days elapsed since `iso`, floored — "173 days" not "173.4 days". */
export function ageInDays(iso: string, now: Date = new Date()): number {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 0;
  return Math.max(0, Math.floor((now.getTime() - then) / 86_400_000));
}

function ageInHours(iso: string, now: Date): number {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 0;
  return Math.max(0, (now.getTime() - then) / 3_600_000);
}

/**
 * True when a Requested (unassigned) consultation has sat longer than its own
 * EstimatedResponseTime. Assigned/InProgress/Completed/Cancelled consultations
 * are never overdue by this definition — they are no longer waiting on an
 * assignment. An unparseable EstimatedResponseTime never counts as overdue.
 */
export function isOverdue(consultation: ConsultationRecord, now: Date = new Date()): boolean {
  if (consultation.status !== ConsultationStatus.Requested) return false;
  const thresholdHours = parseEstimatedResponseTimeHours(consultation.estimatedResponseTime);
  if (thresholdHours === null) return false;
  return ageInHours(consultation.requestedAt, now) > thresholdHours;
}

/** Count of Requested consultations that have exceeded their EstimatedResponseTime. */
export function countOverdueRequested(consultations: readonly ConsultationRecord[], now: Date = new Date()): number {
  return consultations.reduce((count, c) => (isOverdue(c, now) ? count + 1 : count), 0);
}

/** Requested consultations sorted oldest-first (ascending RequestedAt). */
export function sortByRequestedAtAscending<T extends { requestedAt: string }>(consultations: readonly T[]): T[] {
  return [...consultations].sort((a, b) => new Date(a.requestedAt).getTime() - new Date(b.requestedAt).getTime());
}

/**
 * Fetches the Requested-status queue and counts how many have exceeded their
 * EstimatedResponseTime — the number the Sidebar badge (Experts nav item) polls
 * for. A GET /consultations?status=Requested failure surfaces as a thrown error;
 * callers (usePolledQuery) already treat that as "no data this tick", not a crash.
 */
export async function getExpertBacklogAlertCount(): Promise<number> {
  const requested = await getConsultations({ status: ConsultationStatus.Requested });
  return countOverdueRequested(requested);
}
